import { History } from '@mui/icons-material';
import {
    Box,
    Breadcrumbs,
    Divider,
    Link,
    Paper,
    Stack,
    Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import api from '../../api';
import DesktopReleaseDownloadButtons, {
    isDesktopReleaseActive,
} from '../../components/DesktopReleaseDownloadButtons';
import DidNotLoad from '../../components/DidNotLoad';
import Loading from '../../components/Loading';

function formatDate(value) {
    if (!value) {
        return '';
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return '';
    }
    return date.toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: 'long',
        year: 'numeric',
    });
}

function FarmLinkChangelogPage() {
    const navigate = useNavigate();
    const [releases, setReleases] = useState([]);
    const [loading, setLoading] = useState(true);
    const [didNotLoad, setDidNotLoad] = useState(false);

    useEffect(() => {
        api.get('/desktop-releases')
            .then((response) => {
                setReleases(Array.isArray(response?.data?.data) ? response.data.data : []);
                setDidNotLoad(false);
            })
            .catch(() => setDidNotLoad(true))
            .finally(() => setLoading(false));
    }, []);

    if (loading) {
        return <Loading />;
    }
    if (didNotLoad) {
        return <DidNotLoad />;
    }

    const latestActiveId = releases.find((item) => isDesktopReleaseActive(item))?.id;

    return (
        <Box padding={2} maxWidth={920} mx="auto">
            <Breadcrumbs sx={{ mb: 2 }}>
                <Link
                    color="inherit"
                    underline="hover"
                    onClick={(e) => {
                        e.preventDefault();
                        navigate('/admin/integrations');
                    }}
                    href="/admin/integrations"
                >
                    Integrações
                </Link>
                <Typography color="text.primary">Histórico de versões</Typography>
            </Breadcrumbs>

            <Stack direction="row" spacing={1.5} alignItems="center" mb={1}>
                <History color="primary" sx={{ fontSize: 32 }} />
                <Typography variant="h4" component="h1">
                    Histórico do Farm Link Desktop
                </Typography>
            </Stack>
            <Typography variant="body1" color="text.secondary" mb={3}>
                Todas as versões publicadas, com novidades e links de download disponíveis.
            </Typography>

            {releases.length === 0 ? (
                <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                        Nenhuma versão publicada ainda.
                    </Typography>
                </Paper>
            ) : (
                <Stack spacing={2.5}>
                    {releases.map((release) => {
                        const isActive = isDesktopReleaseActive(release);
                        const isCurrent = Boolean(isActive && latestActiveId && latestActiveId === release.id);
                        return (
                            <Paper key={release.id} elevation={2} sx={{ p: { xs: 2.5, md: 3 }, borderRadius: 2 }}>
                                <Stack spacing={1.5}>
                                    <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" gap={1}>
                                        <Box>
                                            <Typography variant="h6">
                                                {release.title || `Versão ${release.version}`}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                v{release.version}
                                                {release.published_at ? ` · ${formatDate(release.published_at)}` : ''}
                                                {isCurrent ? ' · Atual' : ''}
                                                {!isActive ? ' · Indisponível para download' : ''}
                                            </Typography>
                                        </Box>
                                    </Stack>
                                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                        {release.changelog}
                                    </Typography>
                                    {isActive ? (
                                        <>
                                            <Divider />
                                            <DesktopReleaseDownloadButtons release={release} size="medium" />
                                        </>
                                    ) : null}
                                </Stack>
                            </Paper>
                        );
                    })}
                </Stack>
            )}

            <Box mt={3}>
                <Link component={RouterLink} to="/admin/integrations" underline="hover">
                    Voltar para Integrações
                </Link>
            </Box>
        </Box>
    );
}

export default FarmLinkChangelogPage;
